import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/da08pq83256a3o8korb0";

interface Status {
  type: "idle" | "loading" | "success" | "error";
  message: string;
}

export default function ContactForm() {
  const [status, setStatus] = useState<Status>({ type: "idle", message: "" });

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 反垃圾蜜罐字段检测
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot !== "") {
      setStatus({ type: "success", message: "感谢你的订阅！我们会尽快联系你。" });
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    if (!name || !email) {
      setStatus({ type: "error", message: "请填写姓名和邮箱地址后再提交。" });
      return;
    }

    setStatus({ type: "loading", message: "正在提交…" });

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.code ||
        responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus({ type: "success", message: "提交成功！我们会尽快与你联系。" });
        form.reset();
      } else {
        setStatus({ type: "error", message: serverMsg || "提交失败，请稍后重试。" });
      }
    } catch {
      setStatus({ type: "error", message: "网络异常，请稍后重试。" });
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form="日漫资讯订阅/联系表单"
      className="space-y-4"
      noValidate
    >
      <input
        type="text"
        name="website_alt"
        className="form-honeypot-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-950">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={50}
            placeholder="请输入你的称呼"
            className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-950">
            邮箱地址
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            maxLength={100}
            placeholder="name@example.com"
            className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div>
        <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-foreground-950">
          咨询主题
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="订阅日漫资讯快报"
          className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="订阅日漫资讯快报">订阅日漫资讯快报</option>
          <option value="最新话更新提醒">最新话更新提醒</option>
          <option value="休刊复刊提醒">休刊复刊提醒</option>
          <option value="商务合作">商务合作</option>
          <option value="其他咨询">其他咨询</option>
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-950">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          maxLength={500}
          placeholder="请输入你想说的话（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
      </div>

      {status.type === "error" && status.message && (
        <p role="alert" className="rounded-md bg-primary-100 px-4 py-2.5 text-sm text-primary-800">
          {status.message}
        </p>
      )}
      {status.type === "success" && (
        <p role="status" className="rounded-md bg-accent-100 px-4 py-2.5 text-sm text-accent-800">
          {status.message}
        </p>
      )}

      <button
        type="submit"
        disabled={status.type === "loading"}
        className="inline-flex w-full whitespace-nowrap items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status.type === "loading" ? (
          <>
            <i className="ri-loader-4-line animate-spin" /> 提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" /> 立即订阅 / 提交
          </>
        )}
      </button>
    </form>
  );
}